/**
 * StandardizationFormDialog — New / Edit 1 row `tlc_factory.po_standard`.
 * Field: Stack before / GRN before / Stack after / GRN after / Deduct %
 * (nhập %, lưu fraction) / Reason. Stack & GRN đều CHỌN TỪ LIST (v_batchreport_receive,
 * KHÔNG nhập tự do); combo GRN lọc theo stack đã chọn (1 stack có thể nhiều GRN,
 * option GRN hiển thị kèm mt nhận), stack chỉ 1 GRN → auto-fill. Deduct % mặc định
 * 100% khi New. Các cột quality của report là computed (view), KHÔNG lưu ở bảng này.
 */
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useMemo, useState } from 'react'

import type { StandardizationRow, StandardizationSaveBody } from '@/store/api/factoryApi'
import { TEXT_TRUNCATE } from '@/theme/responsive'

interface Props {
  open: boolean
  mode: 'new' | 'edit'
  row: StandardizationRow | null
  saving?: boolean
  /** Stack id distinct của PO (view v_batchreport_receive). */
  stackOptions?: string[]
  /** GRN khả dụng theo từng stack — combo GRN lọc theo stack đã chọn. */
  grnByStack?: Record<string, string[]>
  /** mt theo (stack, GRN) — tra qua `mtKey(stack, grn)` (separator `\0`, khớp build ở StandardizationTab). */
  mtByStackGrn?: Record<string, number | null>
  onClose: () => void
  onSave: (body: StandardizationSaveBody) => void
}

interface Draft {
  stackBefore: string
  grnBefore: string
  deductPct: string // theo %, lưu fraction
  stackAfter: string
  grnAfter: string
  reason: string
}

const EMPTY: Draft = { stackBefore: '', grnBefore: '', deductPct: '', stackAfter: '', grnAfter: '', reason: '' }
// New form: Deduct % default = 100% (deduct_rate = 1).
const NEW_DRAFT: Draft = { ...EMPTY, deductPct: '100' }

const num = (v: string): number | null => {
  const s = (v ?? '').toString().replace(/,/g, '').trim()
  if (!s) return null
  const n = Number(s)
  return Number.isFinite(n) ? n : null
}

const fmtMt = (v: number | null | undefined): string =>
  v == null ? '' : v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

// Key tra mtByStackGrn — PHẢI khớp separator với map build ở StandardizationTab
// (`${stackId}\0${grnId}`, dùng null byte để không đụng stack/grn chứa dấu cách).
const mtKey = (stack: string, grn: string): string => `${stack}\0${grn}`

const compactSx = {
  '& .MuiInputBase-root': { minHeight: 32, fontSize: '0.8125rem' },
  '& .MuiInputBase-input': { py: 0.5, fontSize: '0.8125rem' },
  '& .MuiInputLabel-root': { fontSize: '0.8125rem' },
} as const

export default function StandardizationFormDialog({ open, mode, row, saving, stackOptions = [], grnByStack = {}, mtByStackGrn = {}, onClose, onSave }: Props) {
  const [draft, setDraft] = useState<Draft>(NEW_DRAFT)

  // Hydrate draft khi mở dialog (reset controlled form theo mode/row) — không phải
  // cascading render nên suppress rule set-state-in-effect ([[feedback_hydrate_effect_deps]]).
  useEffect(() => {
    if (!open) return
    if (mode === 'edit' && row) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- hydrate edit draft on open
      setDraft({
        stackBefore: row.stackBefore ?? '',
        grnBefore: row.grnBefore ?? '',
        deductPct: row.deductRate != null ? String(row.deductRate * 100) : '',
        stackAfter: row.stackAfter ?? '',
        grnAfter: row.grnAfter ?? '',
        reason: row.reason ?? '',
      })
    } else {
      setDraft(NEW_DRAFT)
    }
  }, [open, mode, row])

  // Inject value đã lưu vào options nếu chưa có (edit stack/grn cũ không còn trong PO).
  const beforeOpts = useMemo(
    () => (draft.stackBefore && !stackOptions.includes(draft.stackBefore) ? [draft.stackBefore, ...stackOptions] : stackOptions),
    [stackOptions, draft.stackBefore],
  )
  const afterOpts = useMemo(
    () => (draft.stackAfter && !stackOptions.includes(draft.stackAfter) ? [draft.stackAfter, ...stackOptions] : stackOptions),
    [stackOptions, draft.stackAfter],
  )
  // GRN options lọc theo stack đã chọn; inject grn đã lưu nếu thiếu (inline để
  // React Compiler giữ được memoization — không tách hàm ngoài).
  const grnBeforeOpts = useMemo(() => {
    const base = grnByStack[draft.stackBefore] ?? []
    return draft.grnBefore && !base.includes(draft.grnBefore) ? [draft.grnBefore, ...base] : base
  }, [grnByStack, draft.stackBefore, draft.grnBefore])
  const grnAfterOpts = useMemo(() => {
    const base = grnByStack[draft.stackAfter] ?? []
    return draft.grnAfter && !base.includes(draft.grnAfter) ? [draft.grnAfter, ...base] : base
  }, [grnByStack, draft.stackAfter, draft.grnAfter])

  // Đổi stack → reset GRN: stack chỉ 1 GRN thì auto-fill, ngược lại clear nếu GRN
  // hiện tại không thuộc stack mới.
  const pickStack = (side: 'before' | 'after', stack: string) => {
    const grns = grnByStack[stack] ?? []
    const autoGrn = grns.length === 1 ? grns[0] : ''
    setDraft((p) => {
      if (side === 'before') {
        const keep = p.grnBefore && grns.includes(p.grnBefore) ? p.grnBefore : autoGrn
        return { ...p, stackBefore: stack, grnBefore: keep }
      }
      const keep = p.grnAfter && grns.includes(p.grnAfter) ? p.grnAfter : autoGrn
      return { ...p, stackAfter: stack, grnAfter: keep }
    })
  }

  const set = (k: keyof Draft, v: string) => setDraft((p) => ({ ...p, [k]: v }))
  const canSave = draft.stackBefore.trim().length > 0 && !saving

  const handleSave = () => {
    if (!canSave) return
    const pct = num(draft.deductPct)
    onSave({
      stackBefore: draft.stackBefore.trim(),
      grnBefore: draft.grnBefore.trim() || null,
      deductRate: pct == null ? null : pct / 100,
      stackAfter: draft.stackAfter.trim() || null,
      grnAfter: draft.grnAfter.trim() || null,
      reason: draft.reason.trim() || null,
    })
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1, pt: 1.25, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography
          component="span"
          noWrap
          title={`${mode === 'new' ? 'New' : 'Edit'} — Standardization`}
          sx={{ flex: 1, fontWeight: 700, fontSize: '1rem', minWidth: 0, ...TEXT_TRUNCATE }}
        >
          {mode === 'new' ? 'New' : 'Edit'} — Standardization
        </Typography>
        <Button variant="contained" size="small" color="success" onClick={handleSave} disabled={!canSave}
          sx={{ textTransform: 'none', py: 0.25, px: 1 }}>
          Save
        </Button>
        <Button variant="outlined" size="small" onClick={onClose} disabled={saving} sx={{ textTransform: 'none', py: 0.25, px: 1 }}>
          Cancel
        </Button>
      </DialogTitle>
      <Divider />
      <DialogContent dividers sx={{ ...compactSx }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 1.5, pt: 0.5 }}>
          {/* Before — Stack (chọn từ list) + GRN (chọn từ list, kèm mt) */}
          <Autocomplete<string, false, false, false>
            size="small"
            options={beforeOpts}
            value={draft.stackBefore || null}
            onChange={(_, v) => pickStack('before', v ?? '')}
            renderInput={(params) => (
              <TextField {...params} label="Stack before" required fullWidth error={!draft.stackBefore.trim()} placeholder="Select…" />
            )}
          />
          <Autocomplete<string, false, false, false>
            size="small"
            options={grnBeforeOpts}
            value={draft.grnBefore || null}
            onChange={(_, v) => set('grnBefore', v ?? '')}
            disabled={!draft.stackBefore.trim()}
            noOptionsText="No GRN for this stack"
            getOptionLabel={(option) => {
              const mt = mtByStackGrn[mtKey(draft.stackBefore, option)]
              return mt != null ? `${option}  ·  ${fmtMt(mt)} Mt` : option
            }}
            renderOption={(props, option) => (
              <Box component="li" {...props} key={option} sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Box component="span" sx={{ flex: 1 }}>{option}</Box>
                {mtByStackGrn[mtKey(draft.stackBefore, option)] != null && (
                  <Box component="span" sx={{ color: 'text.secondary', fontSize: '0.72rem', fontVariantNumeric: 'tabular-nums' }}>
                    {fmtMt(mtByStackGrn[mtKey(draft.stackBefore, option)])} Mt
                  </Box>
                )}
              </Box>
            )}
            renderInput={(params) => (
              <TextField {...params} label="GRN before" fullWidth placeholder="Select…" />
            )}
          />
          {/* After — Stack (chọn từ list) + GRN (chọn từ list, kèm mt) */}
          <Autocomplete<string, false, false, false>
            size="small"
            options={afterOpts}
            value={draft.stackAfter || null}
            onChange={(_, v) => pickStack('after', v ?? '')}
            renderInput={(params) => (
              <TextField {...params} label="Stack after" fullWidth placeholder="Select…" />
            )}
          />
          <Autocomplete<string, false, false, false>
            size="small"
            options={grnAfterOpts}
            value={draft.grnAfter || null}
            onChange={(_, v) => set('grnAfter', v ?? '')}
            disabled={!draft.stackAfter.trim()}
            noOptionsText="No GRN for this stack"
            getOptionLabel={(option) => {
              const mt = mtByStackGrn[mtKey(draft.stackAfter, option)]
              return mt != null ? `${option}  ·  ${fmtMt(mt)} Mt` : option
            }}
            renderOption={(props, option) => (
              <Box component="li" {...props} key={option} sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Box component="span" sx={{ flex: 1 }}>{option}</Box>
                {mtByStackGrn[mtKey(draft.stackAfter, option)] != null && (
                  <Box component="span" sx={{ color: 'text.secondary', fontSize: '0.72rem', fontVariantNumeric: 'tabular-nums' }}>
                    {fmtMt(mtByStackGrn[mtKey(draft.stackAfter, option)])} Mt
                  </Box>
                )}
              </Box>
            )}
            renderInput={(params) => (
              <TextField {...params} label="GRN after" fullWidth placeholder="Select…" />
            )}
          />
          {/* Amount */}
          <TextField
            label="Deduct %"
            size="small"
            fullWidth
            value={draft.deductPct}
            onChange={(e) => set('deductPct', e.target.value)}
            inputProps={{ inputMode: 'decimal', style: { textAlign: 'right' } }}
            InputProps={{ endAdornment: <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>%</Typography> }}
          />
          <TextField
            label="Reason"
            size="small"
            fullWidth
            multiline
            minRows={2}
            sx={{ gridColumn: { sm: '1 / 3' } }}
            value={draft.reason}
            onChange={(e) => set('reason', e.target.value)}
          />
        </Box>
      </DialogContent>
    </Dialog>
  )
}
